<script setup lang="ts">
import { corteLigado, funilVerPlanos } from "@/utils/eventosDoFunil";
// Tela única MOBILE: carrossel (CarrosselBella + dots) com um card por plano.
// Ordem Basic → Plus → Gold, foco inicial no Plus (ou no Basic com code_share).
// As abas Plano/Créditos ficam no wrapper (NavbarPlansOrCredits), não aqui.
import AssinePlanCardPeriodMobile from "@/components/Assine/AssinePlanCardPeriodMobile.vue";
import CarrosselBella from "@/components/CarrosselBella.vue";
import AssineTitleUpgrade from "@/components/Assine/AssineTitleUpgrade.vue";
import translatersTitle from "@/tradutors/assineTitleUpgrade";
import { formatMoney } from "@/shared/utils/money";

const route = useRoute();
const storePlan = planStore();
const storeUser = userStore();
const storeAssineChoice = assineChoiceStore();
const reactivePlans = computed(() => storePlan.plansType);
const reactiveLocale = computed(() => route.params.language);

const isActiveUser = computed(() => storeUser.isActiveUser);
const isLoggedIn = computed(() => storeUser.isLoggedIn);

const props = defineProps({
  // Pedido atual (repassado aos cards; pode ser {} até a API responder).
  order: {
    type: [Object, Boolean],
    required: false,
    default: () => ({}),
  },
});

// Regras centrais (compartilhadas com o desktop): usamos apenas o `topState`
// para decidir o cabeçalho de topo (mesmo padrão do AssinePlansGridDesktop).
const { topState, isAdminUser } = useAssinePlanRules({
  order: computed(() => props.order),
  isLoggedIn,
  isActiveUser,
  saldoUpgrade: computed(() => storePlan.saldoUpgrade),
});

const translateTitle = computed(() => {
  if (reactiveLocale.value === "pt-br") {
    return translatersTitle.portugues;
  }
  return translatersTitle.ingles;
});

// Topo: estados Gold têm textos próprios; demais usam AssineTitleUpgrade.
const showGoldTopState = computed(
  () => topState.value === "gold-gt-30" || topState.value === "gold-le-30"
);

// Data de validade da assinatura atual (order.finish), para a mensagem Gold >30 dias.
const orderFinish = computed(() => {
  const o = props.order;
  return o && typeof o === "object" ? (o as any).finish ?? "" : "";
});

// Nome do tipo de plano atual do assinante (ex.: "Basic"/"Plus"), anexado ao
// título "Melhore o seu plano <Plano>" no estado active-upgrade.
const currentPlanTypeName = computed(() => {
  const o = props.order;
  return o && typeof o === "object" ? (o as any).plan_type_name ?? "" : "";
});

// Saldo real do assinante formatado (PT: "320,00" / EN: "$ 320,00"), para a mensagem Gold <=30 dias.
const saldoFormatted = computed(
  () =>
    `${reactiveLocale.value === "pt-br" ? "" : "$"} ${formatMoney(
      storePlan.saldoUpgrade,
      { alwaysCents: true }
    )}`
);

// Ordena Basic → Plus → Gold (mesmo padrão dos componentes mobile atuais).
const ORDER_BASIC_FIRST: Record<string, number> = { Basic: 0, Plus: 1, Gold: 2 };
const plansOrderedBasicFirst = computed(() =>
  [...reactivePlans.value].sort(
    (a: any, b: any) =>
      (ORDER_BASIC_FIRST[a.plan_type_name ?? ""] ?? 99) -
      (ORDER_BASIC_FIRST[b.plan_type_name ?? ""] ?? 99)
  )
);

// Foco inicial: Plus (índice 1) por padrão; Basic (0) quando há code_share.
const activeIndex = computed(() => {
  if (route.query.code_share) {
    const basicIdx = plansOrderedBasicFirst.value.findIndex(
      (p: any) => p.plan_type_name === "Basic"
    );
    return basicIdx >= 0 ? basicIdx : 0;
  }
  const plusIdx = plansOrderedBasicFirst.value.findIndex(
    (p: any) => p.plan_type_name === "Plus"
  );
  return plusIdx >= 0 ? plusIdx : 0;
});

function getPlanNameById(planId: number) {
  const plan = reactivePlans.value.find((p: any) => p.plan_type_id === planId);
  return plan ? plan.plan_type_name : "";
}

// GTM: view_cart + begin_checkout no onMounted (reuso 1:1 dos componentes atuais).
function eventGTMViewCart() {
  // Depois do corte da medição: view_item_list no lugar do view_cart.
  if (corteLigado()) {
    funilVerPlanos("planos", route.query.acionador);
    return;
  }
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: "view_cart",
    cart: {
      tipo: "assinatura",
    },
  });
}

function eventGTMBeginCheckout() {
  // Depois do corte, begin_checkout é a abertura do pagamento, não a grade de planos.
  if (corteLigado()) return;
  const chosenId = storeAssineChoice.choicePlanTypeId;
  let name = "";

  if (chosenId && chosenId > 0) {
    name = getPlanNameById(chosenId) || "";
  }

  if (!name) {
    const plus = reactivePlans.value.find(
      (p: any) => p.plan_type_name === "Plus"
    );
    name = plus ? plus.plan_type_name ?? "Plus" : "Plus";
  }

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: "begin_checkout",
    begin_checkout: {
      tipo:
        storeAssineChoice.choiceType !== "credits" ? "assinatura" : "credito",
      acionador: route.query.acionador ?? "link direto",
      currency: reactiveLocale.value === "pt-br" ? "BRL" : "USD",
      plano: name,
    },
  });
}

onMounted(() => {
  eventGTMBeginCheckout();
  setTimeout(() => {
    eventGTMViewCart();
  }, 500);
});
</script>

<template>
  <!-- Sem <ClientOnly>: os preços precisam vir no HTML servido (SSR) também no
       branch mobile (UAs mobile + Googlebot smartphone / mobile-first indexing).
       CarrosselBella e AssinePlanCardPeriodMobile são SSR-safe (todo acesso a
       window/document está em onMounted/handlers). -->
  <div>
    <!-- Admin (level 1/4): o aviso fica no topo da página (assine/index.vue);
         aqui não renderizamos cabeçalho próprio para não conflitar com ele. -->
    <template v-if="!isAdminUser">
      <!-- Topo: estados Gold com textos próprios; demais via AssineTitleUpgrade.
           Mesmo bloco do AssinePlansGridDesktop para não divergir. -->
      <div v-if="showGoldTopState" class="text-center">
        <!-- Gold >30 dias (upgrade bloqueado): parabéns + validade da assinatura. -->
        <template v-if="topState === 'gold-gt-30'">
          <div class="gold-congrats-title">{{ translateTitle.goldGt30Title }}</div>
          <div class="gold-congrats-subtitle">
            {{ translateTitle.goldGt30Validity }} {{ orderFinish }}
          </div>
        </template>
        <!-- Gold <=30 dias (renovação liberada): título + saldo real + validade. -->
        <template v-else>
          <div class="gold-renew-title">{{ translateTitle.goldLe30Title }}</div>
          <div class="gold-renew-subtitle">
            {{ translateTitle.goldLe30Prefix }}
            <strong>{{ translateTitle.goldLe30BalanceLabel }} {{ saldoFormatted }}</strong>
            {{ translateTitle.goldLe30Middle }} {{ orderFinish }}
          </div>
        </template>
      </div>
      <!-- Assinante ativo NÃO-Gold (Plus/Basic): título + saldo REAL para upgrade/renovação.
           Mesmo bloco do AssinePlansGridDesktop (topState === 'active-upgrade'). -->
      <div v-else-if="topState === 'active-upgrade'" class="text-center">
        <div class="active-upgrade-title">
          {{ translateTitle.title }} {{ currentPlanTypeName }}
        </div>
        <div class="active-upgrade-subtitle">
          {{ translateTitle.activeUpgradePrefix }}
          <strong>{{ translateTitle.activeUpgradeBalanceLabel }} {{ saldoFormatted }}</strong>
          {{ translateTitle.activeUpgradeSuffix }}
        </div>
      </div>
      <AssineTitleUpgrade v-else :order="props.order" />
    </template>

    <CarrosselBella
      v-if="plansOrderedBasicFirst.length"
      :cards="plansOrderedBasicFirst"
      :active-index="activeIndex"
      wide-peek
    >
      <template #default="{ item }: { item: any }">
        <AssinePlanCardPeriodMobile
          :planType="item.plan_type_id"
          :order="props.order ?? {}"
        />
      </template>
    </CarrosselBella>
  </div>
</template>

<style scoped lang="css">
/* Cabeçalho de estados Gold (topo). Mesmo estilo do AssinePlansGridDesktop. */
.gold-top-title {
  font-family: "Exo-2";
  font-weight: 700;
  font-size: 35px;
  line-height: 1.1;
  color: #585858;
  text-align: center;
}
.gold-top-subtitle {
  font-family: "Roboto-Regular";
  font-weight: 700;
  font-size: 18px;
  line-height: 25px;
  color: #ff6000;
  text-align: center;
  margin-top: 10px;
}
.gold-top-text {
  font-family: "Exo-2";
  font-size: 14px;
  line-height: 20px;
  color: #5b5858;
  text-align: center;
  margin-top: 6px;
}

/* Gold >30 dias: título de parabéns (laranja) + validade da assinatura (cinza). */
.gold-congrats-title {
  font-family: "Exo-2";
  font-weight: 700;
  font-size: 26px;
  line-height: 1.15;
  color: #ff6000;
  text-align: center;
  margin-top: 50px;
}
.gold-congrats-subtitle {
  font-family: "Exo-2";
  font-weight: 400;
  font-size: 15px;
  line-height: 21px;
  color: #5b5858;
  text-align: center;
  margin-top: 8px;
}

/* Gold <=30 dias: título de renovação (laranja) + saldo/validade (cinza, saldo em negrito). */
.gold-renew-title {
  font-family: "Exo-2";
  font-weight: 700;
  font-size: 25px;
  line-height: 1.15;
  color: #ff6000;
  text-align: center;
  padding-top: 50px;
}
.gold-renew-subtitle {
  font-family: "Exo-2";
  font-weight: 400;
  font-size: 15px;
  line-height: 21px;
  color: #5b5858;
  text-align: center;
  margin-top: 8px;
}
.gold-renew-subtitle strong {
  font-weight: 700;
}

/* Assinante ativo NÃO-Gold (Plus/Basic): título "MELHORE O SEU PLANO" (cinza) +
   subtítulo (laranja) com o saldo real em destaque. Escala mobile do mesmo bloco
   do AssinePlansGridDesktop. */
.active-upgrade-title {
  font-family: "Exo-2";
  font-weight: 700;
  font-size: 28px;
  line-height: 1.1;
  color: #585858;
  text-align: center;
  margin-top: 50px;
}
.active-upgrade-subtitle {
  font-family: "Roboto-Regular";
  font-weight: 400;
  font-size: 16px;
  line-height: 22px;
  color: #ff6000;
  text-align: center;
  margin-top: 8px;
}
.active-upgrade-subtitle strong {
  font-weight: 700;
}

:deep(.carousel__pagination-button::after) {
  display: block;
  content: "";
  width: 12px;
  height: 12px;
  border-radius: 50%;
  opacity: 0.2;
  background-color: #000000;
  margin-right: 15px;
}

:deep(.carousel__pagination-button--active) {
  background-color: #000000;
  width: 13px;
  height: 13px;
  border-radius: 50%;
  margin-right: 15px;
}

/* ===== Tema Gold (aba Renovar/Upgrade da Minha Conta) ===== */
/* A classe .gold-theme vem da raiz do NavBarAccount — em /assine ela não
   existe, então nada aqui muda naquela página. Os CARDS de plano continuam
   como estão; só os textos ao redor entram no tema. */
.gold-theme .gold-top-subtitle,
.gold-theme .gold-congrats-title,
.gold-theme .gold-renew-title,
.gold-theme .active-upgrade-subtitle {
  color: #e9c554;
}

.gold-theme .gold-top-title,
.gold-theme .gold-top-text,
.gold-theme .gold-congrats-subtitle,
.gold-theme .gold-renew-subtitle,
.gold-theme .active-upgrade-title {
  color: #ffffff;
}

/* As bolinhas do carrossel (pretas) são recoloridas no NavBarAccount: elas
   ficam dentro do CarrosselBella e só o :deep de quem tem a classe
   .gold-theme na própria raiz alcança esses elementos. */
</style>
